import React, { useState, useEffect } from 'react';
import { TeacherDataProvider, useTeacherData } from './context/TeacherDataContext';
import { TitleBarSearchContext } from './context/TitleBarSearchContext';
import Dashboard from './components/pages/Dashboard';
import EntryLibrary from './components/pages/EntryLibrary';
import Invoices from './components/pages/Invoices';
import Dancers from './components/pages/Dancers';
import StaffChaperones from './components/pages/StaffChaperones';
import EventRegistration from './components/pages/EventRegistration';
import SchoolProfile from './components/pages/SchoolProfile';
import AccommodationsMeals from './components/pages/AccommodationsMeals';
import InspiringDancer from './components/pages/InspiringDancer';
import Workshops from './components/pages/Workshops';
import { API_BASE } from '../config';
import usePageTitle from '../shared/hooks/usePageTitle';
import logo from '../OSSDF LOGO INVERTED.png';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import {
  useMediaQuery, AppBar, Toolbar, IconButton, Menu, MenuItem, Divider, InputBase,
  BottomNavigation, BottomNavigationAction,
} from '@mui/material';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
  { id: 'dancers', label: 'Dancers', icon: DirectionsRunIcon },
  { id: 'library', label: 'Dance Entries', icon: ListAltIcon },
  { id: 'invoices', label: 'Invoices', icon: ReceiptLongIcon },
  { id: 'staff', label: 'Teachers / Chaperones', icon: GroupsIcon },
];

const PAGES = {
  dashboard: Dashboard,
  library: EntryLibrary,
  invoices: Invoices,
  dancers: Dancers,
  staff: StaffChaperones,
  'event-registration': EventRegistration,
  profile: SchoolProfile,
  accommodations: AccommodationsMeals,
  'inspiring-dancer': InspiringDancer,
  // Reached from the "Workshop Registration" timeline row (like inspiring-dancer).
  workshops: Workshops,
};

// Brand colors pulled from ossdancefest.ca's own stylesheet (their
// .uk-button-primary red and dark nav/heading charcoal), so the teacher
// platform reads as the same brand as the public-facing site.
const SIDEBAR_BG = '#242427';
const ACCENT = '#ED1C24';

export default function TeacherApp() {
  return (
    <TeacherDataProvider>
      <TeacherAppInner />
    </TeacherDataProvider>
  );
}

// The shared data each page shows (keys of TeacherDataContext) — quietly
// re-fetched when the page opens, or the browser tab comes back into view,
// if it's gone stale (see refreshIfStale). Pages not listed load their own
// data whenever they open.
const PAGE_DATA = {
  dashboard: ['school', 'event', 'timeline', 'dancers', 'entries', 'staff', 'feeConfig', 'invoiceSummary', 'locks', 'entryFeePayments'],
  library: ['school', 'event', 'entries', 'dancers', 'groups', 'locks'],
  invoices: ['event', 'timeline', 'dancers', 'entries', 'staff', 'feeConfig', 'invoiceSummary', 'locks', 'entryFeePayments'],
  dancers: ['school', 'event', 'dancers', 'entries', 'groups', 'feeConfig', 'locks', 'entryFeePayments'],
  staff: ['school', 'timeline', 'staff', 'feeConfig', 'locks'],
  accommodations: ['timeline'],
  'inspiring-dancer': ['school', 'event', 'dancers'],
};

// Restores whichever page the teacher was last viewing across a browser
// refresh — PAGES[activePage] already falls back to Dashboard for an
// unrecognized value, so a stale/cleared localStorage value is harmless.
const ACTIVE_PAGE_STORAGE_KEY = 'teacher:activePage';

function TeacherAppInner() {
  usePageTitle('OSSDF Teacher Portal');
  const [activePage, setActivePage] = useState(() => localStorage.getItem(ACTIVE_PAGE_STORAGE_KEY) || 'dashboard');
  const [titleBarSearch, setTitleBarSearch] = useState(null);
  const { school, refreshIfStale } = useTeacherData();
  const isMobile = useMediaQuery('(max-width:768px)');
  const ActiveComponent = PAGES[activePage] || PAGES['dashboard'];

  useEffect(() => {
    localStorage.setItem(ACTIVE_PAGE_STORAGE_KEY, activePage);
  }, [activePage]);

  // Fresh-enough data on page switches and on returning to the tab — no
  // timer, so a portal left open in the background reads nothing.
  useEffect(() => {
    const pageData = PAGE_DATA[activePage] || [];
    refreshIfStale(pageData);
    const onVisible = () => { if (document.visibilityState === 'visible') refreshIfStale(pageData); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [activePage, refreshIfStale]);

  const handleLogout = async () => {
    await fetch(`${API_BASE}/api/teacher-logout`, { method: 'POST', credentials: 'include' });
    window.location.reload();
  };

  // Below 768px: sidebar is replaced entirely by a top title bar + bottom
  // tab bar — the two nav UIs are mutually exclusive by device, never both
  // rendered. See TitleBarSearchContext above for how a page's search box
  // ends up in MobileTopBar instead of its normal in-page position.
  if (isMobile) {
    return (
      <TitleBarSearchContext.Provider value={setTitleBarSearch}>
        {/* 100dvh, not 100vh: mobile Safari's address bar + bottom toolbar
            slide in/out as you scroll, and 100vh is fixed to the viewport
            height with that chrome hidden — so on load, with the toolbar
            still showing, a 100vh flex column overflows past the bottom of
            the actually-visible screen. Combined with overflow:hidden here,
            that pushed MobileBottomNav below the fold with no way to scroll
            to it. 100dvh tracks the real visible viewport as the chrome
            shows/hides, so the bottom nav always stays on-screen. */}
        <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: '#1A1A1A', background: '#FAFAFA' }}>
          <MobileTopBar school={school} activePage={activePage} setActivePage={setActivePage} onLogout={handleLogout} search={titleBarSearch} />
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <ActiveComponent navigateTo={setActivePage} />
          </div>
          <MobileBottomNav activePage={activePage} onChange={setActivePage} />
        </div>
      </TitleBarSearchContext.Provider>
    );
  }

  return (
    <TitleBarSearchContext.Provider value={setTitleBarSearch}>
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: '#1A1A1A', background: '#FAFAFA' }}>

      {/* Sidebar */}
      <div style={{ width: 220, flexShrink: 0, background: SIDEBAR_BG, color: '#fff', display: 'flex', flexDirection: 'column' }}>

        {/* Logo */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={logo} alt="OSS Dancefest" style={{ width: 160, objectFit: 'contain' }} />
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '8px 8px 0' }}>
          {NAV_ITEMS.map(item => {
            const active = activePage === item.id;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActivePage(item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '10px 12px',
                  marginBottom: 2,
                  background: active ? 'rgba(237,28,36,0.18)' : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 15,
                  color: active ? '#fff' : 'rgba(255,255,255,0.6)',
                  fontWeight: active ? 500 : 400,
                  textAlign: 'left',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <Icon style={{ fontSize: 20, flexShrink: 0, opacity: active ? 1 : 0.7 }} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* School info + logout */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Clickable profile row */}
          <button
            onClick={() => setActivePage('profile')}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: '12px 16px', background: activePage === 'profile' ? 'rgba(237,28,36,0.18)' : 'transparent',
              border: 'none', cursor: 'pointer', textAlign: 'left',
            }}
            onMouseEnter={e => { if (activePage !== 'profile') e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
            onMouseLeave={e => { if (activePage !== 'profile') e.currentTarget.style.background = 'transparent'; }}
          >
            {/* Avatar circle */}
            <div style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 700, color: '#fff',
            }}>
              {school ? school.name.charAt(0).toUpperCase() : '?'}
            </div>
            {school && (
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#fff', fontWeight: 500, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{school.name}</div>
                {school.teacherName && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{school.teacherName}</div>}
              </div>
            )}
          </button>

          {/* Logout */}
          <div style={{ padding: '0 12px 12px' }}>
            <button
              onClick={handleLogout}
              style={{
                width: '100%', padding: '7px 0', background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6,
                color: 'rgba(255,255,255,0.55)', fontSize: 12, cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#fff'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
            >
              Log out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <ActiveComponent navigateTo={setActivePage} />
      </div>
    </div>
    </TitleBarSearchContext.Provider>
  );
}

// Mobile-only title bar: logo, then whichever page's search box is
// currently registered via useTitleBarSearch (if any), then an avatar
// button opening a menu with Profile/Log out — the mobile replacement for
// the desktop sidebar's bottom profile row + logout button.
function MobileTopBar({ school, activePage, setActivePage, onLogout, search }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const closeMenu = () => setAnchorEl(null);

  return (
    <AppBar position="static" elevation={0} sx={{ bgcolor: SIDEBAR_BG, flexShrink: 0 }}>
      <Toolbar sx={{ gap: 1.5, minHeight: 56 }}>
        <img src={logo} alt="OSS Dancefest" style={{ height: 32, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {search && (
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', borderRadius: 8, padding: '4px 10px' }}>
              <SearchIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.6)', flexShrink: 0 }} />
              <InputBase
                placeholder={search.placeholder}
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                sx={{
                  color: '#fff', fontSize: 14, flex: 1,
                  '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 },
                }}
              />
            </div>
          )}
        </div>

        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" sx={{ flexShrink: 0, p: 0 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%',
            background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 700, color: '#fff',
          }}>
            {school ? school.name.charAt(0).toUpperCase() : '?'}
          </div>
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          {school && (
            <div style={{ padding: '6px 16px 10px', minWidth: 180 }}>
              <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{school.name}</div>
              {school.teacherName && <div style={{ fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{school.teacherName}</div>}
            </div>
          )}
          <Divider />
          <MenuItem selected={activePage === 'profile'} onClick={() => { closeMenu(); setActivePage('profile'); }}>Profile</MenuItem>
          <MenuItem onClick={() => { closeMenu(); onLogout(); }}>Log out</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

// Mobile-only bottom tab bar — same 5 destinations as the desktop sidebar's
// nav list (NAV_ITEMS), just rendered as icon tabs. A non-nav page (profile,
// event-registration, accommodations) simply leaves every tab unselected
// rather than forcing a wrong one to highlight.
function MobileBottomNav({ activePage, onChange }) {
  const value = NAV_ITEMS.some(item => item.id === activePage) ? activePage : false;
  return (
    <BottomNavigation
      value={value}
      onChange={(e, newValue) => onChange(newValue)}
      showLabels
      sx={{ borderTop: '1px solid #E5E5E5', flexShrink: 0, height: 64 }}
    >
      {NAV_ITEMS.map(item => {
        const Icon = item.icon;
        return (
          <BottomNavigationAction
            key={item.id}
            value={item.id}
            label={item.label}
            icon={<Icon />}
            sx={{
              minWidth: 0,
              color: 'rgba(0,0,0,0.55)',
              '&.Mui-selected': { color: ACCENT },
              '& .MuiBottomNavigationAction-label': { fontSize: 10, lineHeight: 1.2 },
              '& .MuiBottomNavigationAction-label.Mui-selected': { fontSize: 10 },
            }}
          />
        );
      })}
    </BottomNavigation>
  );
}
